// src/App.jsx
import { useState, useEffect, useMemo, useCallback } from "react";
import { onAuthStateChanged, signInWithRedirect, signInWithPopup, getRedirectResult, signOut } from "firebase/auth";
import { auth, googleProvider } from "./lib/firebase";
import { setupOrgIfNeeded, subscribeToItems, subscribeToExternalItems, subscribeToMembers, getMember,
         checkAndAcceptInvite, addItem, updateItem, deleteItem, completeItem, uncompleteItem,
         subscribeToCustomCategories, addCustomCategory, deleteCustomCategory,
         subscribeToTaskTemplates, subscribeToEventBundles,
         subscribeToPendingIncoming, subscribeToDecidedIncoming } from "./lib/db";
import { requestNotificationPermission } from "./lib/notifications";
import { ROLES, CATEGORIES, RECURRENCE_OPTIONS, getCategoryColor, getCategoryLabel, getRole } from "./lib/roles";
import { canSeeItem, canAssign, canTriageIncoming, PERMISSION_LEVELS, isExternal, filterNotes } from "./lib/permissions";
import { RoleBadge } from "./components/RoleBadge";
import { TeamScreen } from "./components/TeamScreen";
import { LibraryScreen } from "./components/LibraryScreen";
import { InboxScreen } from "./components/InboxScreen";
import { useVoiceInput } from "./hooks/useVoiceInput";
// Practice Comms — the other half of the pair. Escalations arrive here from it
// (see the incoming inbox below), and this is the link back.
//
// A LITERAL, not an environment variable, matching how src/lib/firebase.js
// holds authDomain and projectId. This repo has no .env files and no
// REACT_APP_* variable anywhere, so adding one would introduce a build-time
// input that can be absent without anyone noticing — and that is exactly how
// the mirror-image button in Practice Comms behaved: it read an env var that was
// never set in production, so it silently never rendered. A constant either
// exists or fails to compile.
const COMMS_URL = "https://comms.paradisecreekcounseling.com";


// ── Urgency ───────────────────────────────────────────────────────────────
const URGENCY = {
  overdue: { label:"Overdue",    color:"#EF4444", border:"#7F1D1D", textColor:"#FCA5A5" },
  week:    { label:"This week",  color:"#F59E0B", border:"#78350F", textColor:"#FCD34D" },
  month:   { label:"This month", color:"#2DD4BF", border:"#134E4A", textColor:"#5EEAD4" },
  upcoming:{ label:"Upcoming",   color:"#475569", border:"#1E293B", textColor:"#94A3B8" },
};

function getDaysUntil(d) {
  const t=new Date(); t.setHours(0,0,0,0);
  const due=new Date(d+"T00:00:00"); due.setHours(0,0,0,0);
  return Math.round((due-t)/86400000);
}
function urgencyKey(days, completed) {
  if (completed) return "upcoming";
  if (days<0) return "overdue";
  if (days<=7) return "week";
  if (days<=30) return "month";
  return "upcoming";
}
function fmtDue(days, dueStr) {
  if (days<0) return `${Math.abs(days)}d overdue`;
  if (days===0) return "Today";
  if (days===1) return "Tomorrow";
  if (days<=7) return `${days}d`;
  const d = new Date(dueStr+"T00:00:00");
  const thisYear = new Date().getFullYear();
  return d.toLocaleDateString("en-US", d.getFullYear()===thisYear
    ? {month:"short",day:"numeric"}
    : {month:"short",day:"numeric",year:"numeric"});
}

const BLANK = { title:"", category:"", due:"", recurrence:"none", notes:"", assignedTo:"" };

const iStyle = {
  width:"100%", background:"#0F172A", border:"1px solid #2D3748",
  color:"#F1F5F9", padding:"9px 12px", borderRadius:8, fontSize:14,
  outline:"none", fontFamily:"inherit", boxSizing:"border-box"
};
const lStyle = {
  display:"block", fontSize:11, fontWeight:700, color:"#64748B",
  textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:5
};

// ── App ───────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser]           = useState(null);
  const [member, setMember]       = useState(null);   // current user's member record
  const [members, setMembers]     = useState([]);      // all team members
  const [authLoading, setAuthLoading] = useState(true);
  const [items, setItems]         = useState([]);
  const [activeRoleId, setActiveRoleId] = useState("pcc");
  const [activeCatId, setActiveCatId]   = useState("all");
  // View: "mine" | "all" | "team"
  const [viewMode, setViewMode]   = useState("mine");
  const [showCompleted, setShowCompleted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [form, setForm]           = useState(BLANK);
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast]         = useState(null);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [showTeam, setShowTeam]       = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showInbox, setShowInbox]     = useState(false);
  const [incomingPending, setIncomingPending] = useState([]);
  const [incomingDecided, setIncomingDecided] = useState([]);
  const [templates, setTemplates]     = useState([]);
  const [bundles, setBundles]         = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCat, setNewCat] = useState({ label: "", color: "#6366F1" });
  const [teamMemberFilter, setTeamMemberFilter] = useState("everyone");
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);

  const activeRole = getRole(activeRoleId);

  // Reset search when the user switches role or category
  useEffect(() => { setSearchQuery(""); setShowSearch(false); }, [activeRoleId, activeCatId]);

  const visibleRoles = useMemo(() => {
    if (!member) return ROLES;
    const perms = PERMISSION_LEVELS[member.permission];
    if (perms?.externalOnly) return ROLES.filter(r => r.id === "pyha");
    if (perms?.seeAllRoles) return ROLES;
    return ROLES.filter(r => member.allowedRoles?.includes(r.id));
  }, [member]);

  // Force external users onto the pyha tab whenever their permission is resolved
  useEffect(() => {
    if (isExternal(member)) setActiveRoleId("pyha");
  }, [member?.permission]);

  const roleCategories = useMemo(() => {
    const base = CATEGORIES[activeRoleId] || [];
    const custom = customCategories
      .filter(c => c.roleId === activeRoleId)
      .map(c => ({ id: c.id, label: c.label, color: c.color, custom: true }));
    return [...base, ...custom];
  }, [activeRoleId, customCategories]);

  // ── Auth ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try { await getRedirectResult(auth); } catch {}
    };
    init();
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      try {
        if (u) {
          const hasInvite = new URLSearchParams(window.location.search).has("invite");
          if (hasInvite) await checkAndAcceptInvite(u);
          await setupOrgIfNeeded(u);
          const m = await getMember(u.uid);
          setMember(m);
          requestNotificationPermission(u.uid);
        } else {
          setMember(null);
        }
      } catch (err) {
        console.error("Auth setup error:", err);
      } finally {
        setAuthLoading(false);
      }
    });
  }, []);

  // ── Realtime subscriptions ───────────────────────────────────────────────
  // Non-items subscriptions — stable, restart only when user changes
  useEffect(() => {
    if (!user) return;
    const unsub2 = subscribeToMembers(all => {
      setMembers(all);
      const me = all.find(m => m.uid === user.uid);
      if (me) setMember(me);
    });
    const unsub3 = subscribeToCustomCategories(setCustomCategories);
    const unsub4 = subscribeToTaskTemplates(setTemplates);
    const unsub5 = subscribeToEventBundles(setBundles);
    return () => { unsub2(); unsub3(); unsub4(); unsub5(); };
  }, [user]);

  // Items subscription — uses a constrained query for external users so Firestore
  // rules can validate the query server-side. Depends on member permission.
  useEffect(() => {
    if (!user || !member) return;
    const unsub = member.permission === "external"
      ? subscribeToExternalItems(member.uid, setItems)
      : subscribeToItems(setItems);
    return () => unsub();
  }, [user?.uid, member?.uid, member?.permission]);

  // The categories a triager can file an escalation under: PCC's real list,
  // custom ones included. Escalations always become PCC work, so this is not
  // whichever role tab is open. Built from the same source the PCC tab uses —
  // the previous integration kept its own copy of this list in a Cloud
  // Function, the copy went stale, and two valid categories became 400s.
  const pccCategories = useMemo(() => {
    const custom = customCategories
      .filter(c => c.roleId === "pcc")
      .map(c => ({ id: c.id, label: c.label, color: c.color, custom: true }));
    return [...(CATEGORIES.pcc || []), ...custom];
  }, [customCategories]);

  // ── Incoming escalations from Practice Comms ─────────────────────────────
  // Gated on permission because the security rules refuse the read for anyone
  // else: subscribing as staff would produce a permission error on every
  // snapshot rather than an empty list.
  const canTriage = canTriageIncoming(member);
  const signedInUid = user?.uid;
  useEffect(() => {
    if (!signedInUid || !canTriage) { setIncomingPending([]); setIncomingDecided([]); return; }
    const unsubA = subscribeToPendingIncoming(setIncomingPending);
    const unsubB = subscribeToDecidedIncoming(setIncomingDecided);
    return () => { unsubA(); unsubB(); };
  }, [signedInUid, canTriage]);

  // What the badge counts: things a human still has to do. A stalled
  // acceptance — accepted, but the trigger could not create the item — is not
  // pending, and would otherwise sit with nothing anywhere indicating it needs
  // attention.
  const inboxCount = incomingPending.length +
    incomingDecided.filter(d => d.status === "accepted" && !d.itemId).length;

  // ── Toast ────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg, type="success") => {
    setToast({msg,type});
    setTimeout(()=>setToast(null),3500);
  },[]);

  // ── Voice ────────────────────────────────────────────────────────────────
  const { listening, supported: voiceOk, startListening, stopListening } = useVoiceInput({
    onTranscript: setVoiceTranscript,
    onResult: (parsed, raw) => {
      setVoiceTranscript("");
      const defCat = roleCategories[0]?.id || "";
      setForm({ title:parsed.title||"", category:parsed.category||defCat,
                due:parsed.due||"", recurrence:parsed.recurrence||"none",
                notes:"", assignedTo: user?.uid || "" });
      setEditItem(null);
      setShowModal(true);
      if (!parsed.title) showToast(`Heard: "${raw}" — fill in missing fields`,"info");
    }
  });

  // ── Filtered + grouped ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!member) return [];
    const q = searchQuery.trim().toLowerCase();
    return items.filter(item => {
      if (item.roleId !== activeRoleId) return false;
      if (!canSeeItem(member, item)) return false;
      if (activeCatId !== "all" && item.category !== activeCatId) return false;
      if (!showCompleted && item.completed) return false;
      if (viewMode === "mine" && item.assignedTo !== user?.uid) return false;
      if (q && !item.title?.toLowerCase().includes(q) && !item.notes?.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, member, activeRoleId, activeCatId, showCompleted, viewMode, user, searchQuery]);

  const grouped = useMemo(() => {
    const g = { overdue:[], week:[], month:[], upcoming:[] };
    filtered.forEach(item => {
      const d = getDaysUntil(item.due);
      g[urgencyKey(d, item.completed)].push({...item, _days:d});
    });
    Object.values(g).forEach(arr => arr.sort((a,b)=>a._days-b._days));
    return g;
  }, [filtered]);

  // Team view: items grouped by assignee
  const teamGrouped = useMemo(() => {
    if (!member || viewMode !== "team") return {};
    const visible = items.filter(i => i.roleId === activeRoleId && canSeeItem(member, i) && !i.completed);
    const byMember = {};
    visible.forEach(item => {
      const key = item.assignedTo || "unassigned";
      if (!byMember[key]) byMember[key] = [];
      byMember[key].push({...item, _days: getDaysUntil(item.due)});
    });
    return byMember;
  }, [items, member, viewMode, activeRoleId]);

  const urgentCount = grouped.overdue.length + grouped.week.length;
  const monthCount  = grouped.month.length;
  const pulse = urgentCount > 0
    ? `${urgentCount} item${urgentCount!==1?"s":""} need attention${viewMode==="mine"?" on your plate":""}`
    : monthCount > 0 ? `Clear this week · ${monthCount} this month` : "All clear";

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const openAdd = () => {
    const defCat = roleCategories[0]?.id || "";
    setForm({ ...BLANK, category:defCat, assignedTo: user?.uid || "" });
    setEditItem(null);
    setShowModal(true);
  };
  const openEdit = (item) => {
    setForm({ title:item.title, category:item.category, due:item.due,
              recurrence:item.recurrence, notes:item.notes||"", assignedTo:item.assignedTo||"" });
    setEditItem(item);
    setShowModal(true);
  };
  const handleSave = async () => {
    if (!form.title||!form.due) { showToast("Title and due date required","error"); return; }
    try {
      const payload = { ...form, roleId: activeRoleId };
      if (editItem) {
        await updateItem(editItem.id, payload);
        showToast("Updated");
      } else {
        await addItem({ ...payload, completed:false }, user.uid);
        showToast("Added");
        // If assigned to someone else, notify them (handled by Cloud Function)
      }
      setShowModal(false);
    } catch(e) { showToast("Save failed","error"); }
  };
  const handleComplete = async (item) => {
    try {
      if (item.completed) {
        await uncompleteItem(item.id);
        showToast("Marked incomplete");
      } else {
        await completeItem(item, user.uid);
        showToast(item.recurrence!=="none" ? "Done · next occurrence scheduled" : "Marked complete");
      }
    } catch { showToast("Error","error"); }
  };
  const handleAddCat = async () => {
    if (!newCat.label.trim()) return;
    try {
      await addCustomCategory(activeRoleId, newCat.label.trim(), newCat.color);
      setNewCat({ label: "", color: "#6366F1" });
      setShowAddCat(false);
      showToast("Category added");
    } catch { showToast("Failed to add category", "error"); }
  };
  const handleDeleteCat = async (id) => {
    try {
      await deleteCustomCategory(id);
      if (activeCatId === id) setActiveCatId("all");
      showToast("Category removed", "info");
    } catch { showToast("Failed to remove category", "error"); }
  };

  const handleDelete = async (id) => {
    try { await deleteItem(id); showToast("Removed","info"); }
    catch { showToast("Error","error"); }
  };

  const getMemberName = (uid) => {
    if (!uid) return "Unassigned";
    if (uid === user?.uid) return "You";
    return members.find(m=>m.uid===uid)?.displayName || "Unknown";
  };

  // ── Login ──────────────────────────────────────────────────────────────
  if (authLoading) return (
    <div style={{minHeight:"100vh",background:"#0F172A",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{color:"#2DD4BF",fontSize:14}}>Loading…</div>
    </div>
  );

  if (!user || !member) return (
    <div style={{minHeight:"100vh",background:"#0F172A",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{textAlign:"center",maxWidth:360}}>
        <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.2em",color:"#2DD4BF",marginBottom:12,textTransform:"uppercase"}}>Paradise Creek</div>
        <div style={{fontSize:28,fontWeight:800,color:"#F1F5F9",marginBottom:8}}>Practice Brain</div>
        <div style={{fontSize:14,color:"#64748B",marginBottom:40,lineHeight:1.6}}>
          Team operations hub for Paradise Creek Counseling & PYHA.<br/>Sign in with your Google account.
        </div>
        <button onClick={()=>{
  const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (mobile) signInWithRedirect(auth,googleProvider);
  else signInWithPopup(auth,googleProvider);
}} style={{
          display:"flex",alignItems:"center",justifyContent:"center",gap:10,
          background:"#fff",color:"#1C2333",border:"none",
          padding:"13px 28px",borderRadius:10,fontSize:15,fontWeight:700,
          cursor:"pointer",width:"100%",boxShadow:"0 4px 20px rgba(0,0,0,0.3)"
        }}>
          <svg width="20" height="20" viewBox="0 0 48 48">
            <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.5 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.1 7.9 3l5.7-5.7C34.2 6.5 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.2 0-9.7-3.4-11.3-8L6 33.3C9.4 39.7 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.2C37 36.9 44 31 44 24c0-1.3-.1-2.6-.4-3.9z"/>
          </svg>
          Sign in with Google
        </button>
        {!user && <div style={{marginTop:12,fontSize:12,color:"#374151"}}>Access is by invitation only</div>}
        {user && !member && <div style={{marginTop:12,fontSize:12,color:"#EF4444"}}>Your account isn't linked to a team yet. Ask your admin for an invite.</div>}
      </div>
    </div>
  );

  // ── Main UI ────────────────────────────────────────────────────────────
  return (
    <div style={{minHeight:"100vh",background:"#0F172A",fontFamily:"'Inter',system-ui,sans-serif",color:"#F1F5F9",paddingBottom:"calc(80px + env(safe-area-inset-bottom))"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        input,select,textarea{font-family:inherit;}
        @keyframes fadeDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        input[type=date]::-webkit-calendar-picker-indicator{filter:invert(0.4);}
        ::-webkit-scrollbar{width:8px}::-webkit-scrollbar-track{background:#0F172A}::-webkit-scrollbar-thumb{background:#334155;border-radius:4px}::-webkit-scrollbar-thumb:hover{background:#475569}
      `}</style>

      {toast && (
        <div style={{position:"fixed",top:16,left:"50%",transform:"translateX(-50%)",zIndex:1000,
          background:toast.type==="error"?"#7F1D1D":toast.type==="info"?"#1E3A5F":"#134E4A",
          color:"#fff",padding:"10px 20px",borderRadius:8,fontSize:13,fontWeight:500,
          boxShadow:"0 4px 20px rgba(0,0,0,0.5)",animation:"fadeDown 0.2s ease",whiteSpace:"nowrap",maxWidth:"90vw"}}>
          {toast.msg}
        </div>
      )}

      {showTeam && (
        <TeamScreen currentMember={member} members={members}
          onClose={()=>setShowTeam(false)} showToast={showToast} />
      )}

      {showInbox && canTriage && (
        <InboxScreen
          pending={incomingPending}
          decided={incomingDecided}
          members={members}
          categories={pccCategories}
          member={member}
          onClose={()=>setShowInbox(false)}
          showToast={showToast}
        />
      )}

      {showLibrary && (
        <LibraryScreen
          activeRole={activeRole}
          roleCategories={roleCategories}
          members={members}
          member={member}
          user={user}
          templates={templates}
          bundles={bundles}
          onClose={()=>setShowLibrary(false)}
          showToast={showToast}
        />
      )}

      {/* Header */}
      <div style={{position:"sticky",top:0,zIndex:50,background:"#1C2333",borderBottom:"1px solid #2D3748",paddingTop:"env(safe-area-inset-top)"}}>

        {/* Top bar */}
        <div style={{padding:"10px 16px",display:"flex",alignItems:"center",gap:10}}>
          <RoleBadge role={activeRole} size={36} />
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:10,fontWeight:800,letterSpacing:"0.14em",color:activeRole.accent,textTransform:"uppercase"}}>{activeRole.shortName}</div>
            <div style={{fontSize:15,fontWeight:800,color:"#F1F5F9"}}>Practice Brain</div>
          </div>
          {/* User avatar */}
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {voiceOk && (
              <button onClick={listening?stopListening:startListening}
                style={{background:listening?activeRole.accent:"#2D3748",color:listening?"#0F172A":"#94A3B8",
                  border:"none",borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:15,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  animation:listening?"pulse 1.5s infinite":"none"}} title="Voice input">🎤</button>
            )}
            {canAssign(member) && (
              <button onClick={()=>setShowTeam(true)}
                style={{background:"#2D3748",color:"#94A3B8",border:"none",borderRadius:8,
                  width:34,height:34,cursor:"pointer",fontSize:15,display:"flex",alignItems:"center",justifyContent:"center"}}
                title="Team">👥</button>
            )}
            <button onClick={()=>{setShowSearch(s=>{if(s){setSearchQuery("");}return !s;});}}
              style={{background:showSearch?activeRole.accentBg:"#2D3748",
                color:showSearch?activeRole.accent:"#94A3B8",
                border:"none",borderRadius:8,width:34,height:34,cursor:"pointer",fontSize:15,
                display:"flex",alignItems:"center",justifyContent:"center"}}
              title="Search">🔍</button>
            {!isExternal(member) && (
              <button onClick={openAdd}
                style={{background:activeRole.accent,color:"#0F172A",border:"none",
                  padding:"7px 13px",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer"}}>+ Add</button>
            )}
            {/* Cross-link to Practice Comms. Matches its counterpart in
                FUNCTION and WORDING rather than appearance: that app uses CSS
                classes and its own palette, this one is inline-styled and dark,
                so copying the markup would look foreign here. Geometry follows
                the "+ Add" button; the quiet palette says navigation rather
                than primary action.

                "Signs in separately" is not decoration. This app signs in with
                Google and Practice Comms signs in with email and password, so
                arriving there logged in here means nothing — saying so up front
                is cheaper than the confusion of a login screen that looks like
                a fault. */}
            <a href={COMMS_URL}
              title="Practice Comms — faxes and voicemail. Signs in separately from this app."
              style={{background:"#2D3748",color:"#94A3B8",border:"none",
                padding:"7px 11px",borderRadius:8,fontSize:13,fontWeight:700,
                cursor:"pointer",textDecoration:"none",whiteSpace:"nowrap"}}>Comms</a>
            {user.photoURL
              ? <img src={user.photoURL} alt="" style={{width:30,height:30,borderRadius:"50%",cursor:"pointer"}} onClick={()=>signOut(auth)} title="Sign out" />
              : <button onClick={()=>signOut(auth)} style={{background:"#2D3748",border:"none",color:"#94A3B8",cursor:"pointer",borderRadius:"50%",width:30,height:30,fontSize:12}}>⎋</button>
            }
          </div>
        </div>

        {/* Search bar */}
        {showSearch && (
          <div style={{padding:"6px 16px 8px",display:"flex",alignItems:"center",gap:8}}>
            <input
              autoFocus
              value={searchQuery}
              onChange={e=>setSearchQuery(e.target.value)}
              onFocus={()=>setSearchFocused(true)}
              onBlur={()=>setSearchFocused(false)}
              placeholder="Search tasks…"
              style={{flex:1,background:"#0F172A",
                border:`1px solid ${searchFocused?"#2DD4BF":"#2D3748"}`,
                color:"#F1F5F9",padding:"7px 11px",borderRadius:8,fontSize:13,outline:"none"}}
            />
            <button onClick={()=>{setShowSearch(false);setSearchQuery("");}}
              style={{background:"transparent",border:"none",color:"#64748B",cursor:"pointer",
                fontSize:18,lineHeight:1,padding:"0 4px",flexShrink:0}}>✕</button>
          </div>
        )}

        {/* Voice transcript */}
        {listening && (
          <div style={{padding:"5px 16px 8px",fontSize:12,color:activeRole.accent,background:activeRole.accentBg}}>
            🎤 {voiceTranscript || "Listening… 'Add item: [title], [category], due [date]'"}
          </div>
        )}

        {/* Pulse */}
        <div style={{
          margin:"0 16px 8px",padding:"7px 12px",borderRadius:8,fontSize:12,fontWeight:600,
          background:urgentCount>0?"rgba(239,68,68,0.1)":activeRole.accentBg,
          border:`1px solid ${urgentCount>0?"#7F1D1D":activeRole.accent+"33"}`,
          color:urgentCount>0?"#FCA5A5":activeRole.accent
        }}>{urgentCount>0?"⚠️":"✓"} {pulse}</div>

        {/* Role tabs + Library tab */}
        <div style={{display:"flex",borderBottom:"1px solid #2D3748",overflowX:"auto"}}>
          {visibleRoles.map(role => (
            <button key={role.id} onClick={()=>{setActiveRoleId(role.id);setActiveCatId("all");setTeamMemberFilter("everyone");setShowLibrary(false);}}
              style={{background:"transparent",border:"none",
                borderBottom:activeRoleId===role.id&&!showLibrary?`2px solid ${role.accent}`:"2px solid transparent",
                color:activeRoleId===role.id&&!showLibrary?role.accent:"#64748B",
                padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",
                whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:5}}>
              <RoleBadge role={role} size={14}/>{role.shortName}
            </button>
          ))}
          {canTriage && (
            <button onClick={()=>setShowInbox(true)}
              style={{background:"transparent",border:"none",
                borderBottom:showInbox?"2px solid #2DD4BF":"2px solid transparent",
                color:showInbox?"#2DD4BF":"#64748B",
                padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",
                whiteSpace:"nowrap",marginLeft:"auto",
                display:"flex",alignItems:"center",gap:6}}>
              📥 Inbox
              {inboxCount > 0 && (
                <span style={{background:"#7F1D1D",color:"#FCA5A5",fontSize:10,fontWeight:800,
                  minWidth:17,height:17,borderRadius:9,display:"inline-flex",
                  alignItems:"center",justifyContent:"center",padding:"0 5px"}}>
                  {inboxCount}
                </span>
              )}
            </button>
          )}
          {!isExternal(member) && (
            <button onClick={()=>setShowLibrary(true)}
              style={{background:"transparent",border:"none",
                borderBottom:showLibrary?"2px solid #94A3B8":"2px solid transparent",
                color:showLibrary?"#F1F5F9":"#64748B",
                padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",
                whiteSpace:"nowrap",marginLeft:canTriage?0:"auto"}}>
              📚 Library
            </button>
          )}
        </div>

        {/* View toggle + category chips */}
        <div style={{display:"flex",gap:6,padding:"8px 16px",overflowX:"auto",alignItems:"center"}}>
          {/* View mode — hidden for external users (they always see only their own tasks) */}
          {!isExternal(member) && <>
            {["mine","all",...(canAssign(member)?["team"]:[])].map(mode => (
              <button key={mode} onClick={()=>{setViewMode(mode);setTeamMemberFilter("everyone");}}
                style={{background:viewMode===mode?activeRole.accent:"#1E293B",
                  color:viewMode===mode?"#0F172A":"#64748B",
                  border:"none",padding:"4px 10px",borderRadius:20,
                  fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                {mode==="mine"?"My tasks":mode==="all"?"All tasks":"Team view"}
              </button>
            ))}
            <div style={{width:1,height:16,background:"#2D3748",flexShrink:0}} />
          </>}
          {/* Category chips */}
          <button onClick={()=>setActiveCatId("all")}
            style={{background:activeCatId==="all"?activeRole.accent:"#1E293B",
              color:activeCatId==="all"?"#0F172A":"#64748B",
              border:"none",padding:"4px 10px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>All</button>
          {roleCategories.map(cat=>(
            <div key={cat.id} style={{display:"flex",alignItems:"center",gap:2,flexShrink:0}}>
              <button onClick={()=>setActiveCatId(cat.id)}
                style={{background:activeCatId===cat.id?cat.color:"#1E293B",
                  color:activeCatId===cat.id?"#fff":"#64748B",
                  border:"none",padding:"4px 10px",borderRadius:20,fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
                {cat.label}
              </button>
              {cat.custom && canAssign(member) && (
                <button onClick={()=>handleDeleteCat(cat.id)}
                  style={{background:"transparent",border:"none",color:"#475569",cursor:"pointer",fontSize:11,padding:"0 2px",lineHeight:1}}>×</button>
              )}
            </div>
          ))}
          {canAssign(member) && !showAddCat && (
            <button onClick={()=>setShowAddCat(true)}
              style={{background:"transparent",border:"1px dashed #2D3748",color:"#475569",
                padding:"4px 8px",borderRadius:20,fontSize:11,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>+ cat</button>
          )}
          {showAddCat && (
            <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
              <input value={newCat.label} onChange={e=>setNewCat(n=>({...n,label:e.target.value}))}
                onKeyDown={e=>e.key==="Enter"&&handleAddCat()}
                placeholder="Name" autoFocus
                style={{background:"#0F172A",border:"1px solid #2D3748",color:"#F1F5F9",
                  padding:"3px 8px",borderRadius:8,fontSize:11,width:80,outline:"none"}}/>
              <input type="color" value={newCat.color} onChange={e=>setNewCat(n=>({...n,color:e.target.value}))}
                style={{width:24,height:24,border:"none",borderRadius:4,cursor:"pointer",background:"none",padding:0}}/>
              <button onClick={handleAddCat}
                style={{background:activeRole.accent,color:"#0F172A",border:"none",
                  padding:"3px 8px",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer"}}>Add</button>
              <button onClick={()=>{setShowAddCat(false);setNewCat({label:"",color:"#6366F1"});}}
                style={{background:"transparent",border:"none",color:"#475569",cursor:"pointer",fontSize:13}}>✕</button>
            </div>
          )}
          <button onClick={()=>setShowCompleted(s=>!s)}
            style={{background:"transparent",border:"1px solid #2D3748",color:"#475569",
              padding:"4px 8px",borderRadius:20,fontSize:11,cursor:"pointer",whiteSpace:"nowrap",marginLeft:"auto"}}>
            {showCompleted?"Hide done":"Show done"}
          </button>
        </div>
      </div>

      {/* ── TEAM VIEW ───────────────────────────────────────────────────── */}
      {viewMode === "team" ? (
        <div style={{padding:"8px 16px 0"}}>
          {/* Member filter chips */}
          <div style={{display:"flex",gap:6,overflowX:"auto",marginBottom:12,paddingBottom:4}}>
            {["everyone", ...members.map(m=>m.uid)].map(uid => {
              const m = members.find(m=>m.uid===uid);
              const label = uid==="everyone" ? "Everyone" : uid===user.uid ? "You" : m?.displayName || "Unknown";
              const active = teamMemberFilter === uid;
              return (
                <button key={uid} onClick={()=>setTeamMemberFilter(uid)}
                  style={{background:active?activeRole.accent:"#1E293B",
                    color:active?"#0F172A":"#64748B",border:"none",
                    padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:700,
                    cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                  {label}
                </button>
              );
            })}
          </div>
          {Object.entries(teamGrouped)
            .filter(([uid]) => teamMemberFilter === "everyone" || uid === teamMemberFilter)
            .map(([uid, taskList]) => {
            const m = members.find(m=>m.uid===uid);
            const name = uid==="unassigned" ? "Unassigned" : uid===user.uid ? "You" : m?.displayName || "Unknown";
            const overdueCount = taskList.filter(i=>i._days<0).length;
            return (
              <div key={uid} style={{marginBottom:24}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
                  {m?.photoURL
                    ? <img src={m.photoURL} alt="" style={{width:24,height:24,borderRadius:"50%"}}/>
                    : <div style={{width:24,height:24,borderRadius:"50%",background:"#2D3748",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#94A3B8"}}>{name[0]}</div>
                  }
                  <span style={{fontSize:13,fontWeight:700,color:"#F1F5F9"}}>{name}</span>
                  <span style={{fontSize:11,color:"#475569"}}>({taskList.length} open{overdueCount>0?`, ${overdueCount} overdue`:""})</span>
                  {overdueCount>0 && <span style={{fontSize:10,color:"#EF4444",fontWeight:700}}>⚠️</span>}
                </div>
                {taskList.sort((a,b)=>a._days-b._days).map(item => (
                  <TaskRow key={item.id} item={item} member={member} user={user} members={members}
                    expanded={expandedId===item.id} onExpand={()=>setExpandedId(expandedId===item.id?null:item.id)}
                    onComplete={()=>handleComplete(item)} onEdit={()=>openEdit(item)} onDelete={()=>handleDelete(item.id)}
                    getMemberName={getMemberName} activeRole={activeRole} />
                ))}
              </div>
            );
          })}
          {Object.keys(teamGrouped).length===0 && <EmptyState label="No open tasks" sub="All caught up!" />}
          {Object.keys(teamGrouped).length>0 && teamMemberFilter!=="everyone" && !teamGrouped[teamMemberFilter] && <EmptyState label="No open tasks" sub="All caught up for this person!" />}
        </div>
      ) : (
        /* ── URGENCY BANDS VIEW ────────────────────────────────────────── */
        <div style={{padding:"16px 16px 0"}}>
          {Object.entries(URGENCY).map(([key,cfg])=>{
            const band = grouped[key];
            if (!band.length) return null;
            return (
              <div key={key} style={{marginBottom:24}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:cfg.color,flexShrink:0}}/>
                  <span style={{fontSize:10,fontWeight:800,letterSpacing:"0.14em",color:cfg.color,textTransform:"uppercase"}}>{cfg.label}</span>
                  <span style={{fontSize:10,color:"#374151"}}>({band.length})</span>
                </div>
                {band.map(item=>(
                  <TaskRow key={item.id} item={item} member={member} user={user} members={members}
                    expanded={expandedId===item.id} onExpand={()=>setExpandedId(expandedId===item.id?null:item.id)}
                    onComplete={()=>handleComplete(item)} onEdit={()=>openEdit(item)} onDelete={()=>handleDelete(item.id)}
                    getMemberName={getMemberName} activeRole={activeRole} urgencyCfg={cfg} />
                ))}
              </div>
            );
          })}
          {filtered.filter(i=>!i.completed).length===0 && (
            <EmptyState
              label={searchQuery.trim() ? "No results" : viewMode==="mine" ? "Your plate is clear" : "Nothing here"}
              sub={searchQuery.trim() ? `No tasks match "${searchQuery.trim()}"` : "Tap + Add to create an item"} />
          )}
        </div>
      )}

      {/* ── ADD / EDIT MODAL ──────────────────────────────────────────────── */}
      {showModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div style={{background:"#1E293B",borderRadius:"16px 16px 0 0",padding:24,width:"100%",maxWidth:540,
            border:"1px solid #2D3748",boxShadow:"0 -20px 60px rgba(0,0,0,0.5)",
            animation:"fadeDown 0.2s ease",maxHeight:"92vh",overflowY:"auto"}}>
            <div style={{fontSize:15,fontWeight:800,marginBottom:20,color:"#F1F5F9"}}>
              {editItem?"Edit item":"New item"}
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div>
                <label style={lStyle}>Title</label>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}
                  placeholder="What needs to happen?" style={iStyle} autoFocus />
              </div>
              <div style={{display:"flex",gap:10}}>
                <div style={{flex:1}}>
                  <label style={lStyle}>Category</label>
                  <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} style={iStyle}>
                    {roleCategories.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div style={{flex:1}}>
                  <label style={lStyle}>Recurs</label>
                  <select value={form.recurrence} onChange={e=>setForm(f=>({...f,recurrence:e.target.value}))} style={iStyle}>
                    {RECURRENCE_OPTIONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={lStyle}>Due date</label>
                <input type="date" value={form.due} onChange={e=>setForm(f=>({...f,due:e.target.value}))} style={iStyle}/>
              </div>
              {/* Assign to — only show if user can assign */}
              {canAssign(member) && (
                <div>
                  <label style={lStyle}>Assign to</label>
                  <select value={form.assignedTo} onChange={e=>setForm(f=>({...f,assignedTo:e.target.value}))} style={iStyle}>
                    <option value="">Unassigned</option>
                    {members.map(m=>(
                      <option key={m.uid} value={m.uid}>
                        {m.uid===user.uid?"Me":m.displayName||m.email}
                        {m.permission==="owner"||m.permission==="manager" ? ` (${PERMISSION_LEVELS[m.permission]?.label})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label style={lStyle}>Notes</label>
                <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
                  placeholder="Context, contacts, what to watch for…"
                  style={{...iStyle,height:80,resize:"vertical"}}/>
              </div>
            </div>
            <div style={{display:"flex",gap:10,marginTop:20}}>
              <button onClick={()=>setShowModal(false)}
                style={{flex:1,background:"#2D3748",color:"#94A3B8",border:"none",padding:12,borderRadius:10,fontSize:14,cursor:"pointer",fontWeight:600}}>
                Cancel
              </button>
              <button onClick={handleSave}
                style={{flex:2,background:activeRole.accent,color:"#0F172A",border:"none",padding:12,borderRadius:10,fontSize:14,fontWeight:800,cursor:"pointer"}}>
                {editItem?"Save changes":"Add item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Task row component ─────────────────────────────────────────────────────
function TaskRow({ item, member, user, members, expanded, onExpand, onComplete, onEdit, onDelete, getMemberName, activeRole, urgencyCfg }) {
  const days = item._days ?? getDaysUntil(item.due);
  const cfg = urgencyCfg || URGENCY[urgencyKey(days, item.completed)];
  const catColor = getCategoryColor(item.roleId, item.category);
  const dueLabel = fmtDue(days, item.due);
  const isAssignedToMe = item.assignedTo === user?.uid;
  const assigneeName = getMemberName(item.assignedTo);
  const notes = filterNotes(item.notes, member);

  return (
    <div style={{background:"#1E293B",borderRadius:8,overflow:"hidden",marginBottom:5,
      border:`1px solid ${item.completed?"#1E293B":cfg.border}`,
      opacity:item.completed?0.45:1,transition:"opacity 0.2s"}}>
      <div style={{display:"flex",alignItems:"center"}}>
        <div style={{width:3,alignSelf:"stretch",background:item.completed?"#374151":cfg.color,flexShrink:0}}/>
        <button onClick={onComplete}
          style={{width:44,height:44,background:"transparent",border:"none",cursor:"pointer",
            flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{
            width:24,height:24,borderRadius:"50%",
            border:`2.5px solid ${item.completed?"#2DD4BF":"#94A3B8"}`,
            background:item.completed?"#2DD4BF":"transparent",
            display:"flex",alignItems:"center",justifyContent:"center",
            color:item.completed?"#0F172A":"transparent",
            fontSize:13,fontWeight:900,lineHeight:1,flexShrink:0,
          }}>✓</div>
        </button>
        <button onClick={onExpand}
          style={{flex:1,background:"transparent",border:"none",cursor:"pointer",textAlign:"left",padding:"8px 6px",minWidth:0}}>
          <div style={{fontSize:13,fontWeight:600,color:item.completed?"#475569":"#F1F5F9",
            textDecoration:item.completed?"line-through":"none",lineHeight:1.3,marginBottom:3}}>
            {item.title}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
            <span style={{fontSize:10,fontWeight:700,padding:"1px 7px",borderRadius:8,
              background:catColor+"22",color:catColor}}>
              {getCategoryLabel(item.roleId,item.category)}
            </span>
            {/* Assignee chip */}
            {item.assignedTo && (
              <span style={{fontSize:10,fontWeight:600,padding:"1px 7px",borderRadius:8,
                background: isAssignedToMe ? (activeRole.accentBg) : "#1E293B",
                color: isAssignedToMe ? activeRole.accent : "#64748B",
                border:`1px solid ${isAssignedToMe ? activeRole.accent+"44" : "#2D3748"}`}}>
                {isAssignedToMe?"→ You":`→ ${assigneeName}`}
              </span>
            )}
            {item.recurrence!=="none"&&<span style={{fontSize:10,color:"#475569"}}>🔁</span>}
          </div>
        </button>
        <div style={{display:"flex",alignItems:"center",gap:3,paddingRight:8,flexShrink:0}}>
          {dueLabel&&<span style={{fontSize:11,fontWeight:700,color:cfg.textColor,whiteSpace:"nowrap"}}>{dueLabel}</span>}
          {!isExternal(member) && <>
            <button onClick={onEdit} style={{background:"transparent",border:"none",color:"#374151",cursor:"pointer",padding:"3px",fontSize:13}}>✏️</button>
            <button onClick={onDelete} style={{background:"transparent",border:"none",color:"#374151",cursor:"pointer",padding:"3px",fontSize:13}}>🗑</button>
          </>}
        </div>
      </div>
      {expanded&&(
        <div style={{borderTop:"1px solid #2D3748",padding:"10px 12px 10px 43px",fontSize:12,color:"#CBD5E1",lineHeight:1.6}}>
          {notes||<span style={{color:"#64748B",fontStyle:"italic"}}>No notes</span>}
          <div style={{marginTop:6,fontSize:11,color:"#94A3B8"}}>
            Due: {new Date(item.due+"T00:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric",year:"numeric"})}
            {item.recurrence!=="none"&&` · Recurs ${RECURRENCE_OPTIONS.find(r=>r.value===item.recurrence)?.label?.toLowerCase()}`}
            {item.assignedTo&&` · Assigned to ${assigneeName}`}
            {item.createdBy&&item.createdBy!==user?.uid&&` · Added by ${getMemberName(item.createdBy)}`}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ label, sub }) {
  return (
    <div style={{textAlign:"center",padding:"60px 0",color:"#374151"}}>
      <div style={{fontSize:28,marginBottom:10}}>✓</div>
      <div style={{fontSize:14,fontWeight:600,color:"#475569"}}>{label}</div>
      <div style={{fontSize:12,color:"#374151",marginTop:4}}>{sub}</div>
    </div>
  );
}
